import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, Mail, Copy, Trash2, ShieldOff, ShieldCheck, Send } from "lucide-react";

/**
 * Admin-side manager for a colony's portal logins.
 * Multiple users can be attached to the same colony; each user can
 * belong to multiple colonies.
 */
export default function ColonyPortalUsersDialog({ colony, onOpenChange }) {
  const open = !!colony;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", send_invite: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!colony) return;
    setLoading(true);
    try {
      const r = await api.get(`/mgmt/colonies/${colony.id}/users`);
      setUsers(r.data?.users || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [colony]);

  useEffect(() => {
    if (open) {
      load();
      setForm({ email: "", name: "", password: "", send_invite: true });
    }
  }, [open, load]);

  const add = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email) return toast.error("Email is required");
    if (!form.send_invite && !form.password) {
      return toast.error("Either send an email invite OR set a temp password.");
    }
    setSaving(true);
    try {
      const r = await api.post(`/mgmt/colonies/${colony.id}/users`, {
        email,
        name: form.name.trim(),
        password: form.password || undefined,
        send_invite: form.send_invite,
      });
      if (r.data?.invite_link) {
        if (r.data.email_sent) {
          toast.success(`Invite email sent to ${email}`);
        } else {
          toast.warning(`Invite created but email couldn't send${r.data.email_error ? ` (${r.data.email_error})` : ""}. Copy the link below and text it to them.`);
        }
      } else {
        toast.success(`User added${form.password ? " with a temporary password" : ""}.`);
      }
      setForm({ email: "", name: "", password: "", send_invite: true });
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resend = async (u) => {
    try {
      const r = await api.post(`/mgmt/colonies/${colony.id}/users/${u.id}/resend-invite`);
      if (r.data?.email_sent) toast.success(`Invite re-sent to ${u.email}`);
      else toast.warning(`Email failed${r.data?.email_error ? ` (${r.data.email_error})` : ""}. Copy the link below.`);
      if (r.data?.invite_link) {
        try {
          await navigator.clipboard.writeText(r.data.invite_link);
          toast.message("Invite link copied to clipboard.");
        } catch (_) { /* clipboard blocked — user can copy from toast */ }
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.patch(`/mgmt/colonies/${colony.id}/users/${u.id}`, { disabled: !u.disabled });
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They'll lose access to ${colony.name} immediately.`)) return;
    try {
      await api.delete(`/mgmt/colonies/${colony.id}/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-lg" data-testid="colony-users-dialog">
        <DialogHeader>
          <DialogTitle>Portal logins — {colony?.name}</DialogTitle>
          <DialogDescription>
            Give people at the colony their own read-only login. They&apos;ll see the statement + can file service requests. You can add as many as you like.
          </DialogDescription>
        </DialogHeader>

        {/* Add form */}
        <div className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Add a user</Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Email *"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="colony-user-form-email"
            />
            <Input
              placeholder="Name (optional)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="colony-user-form-name"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.send_invite}
                onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                data-testid="colony-user-form-invite"
              />
              Email an activation link
            </label>
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Or set temp password (optional)"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="colony-user-form-password"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={add} disabled={saving || !form.email.trim()} className="rounded-sm" data-testid="colony-user-form-add">
              <UserPlus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </div>

        {/* Existing users */}
        <div className="border border-border rounded-sm max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && users.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="colony-users-empty">
              No portal users yet.
            </div>
          )}
          {users.map((u) => (
            <div
              key={u.id}
              className="px-3 py-2 flex items-center justify-between border-b border-border last:border-b-0 gap-2"
              data-testid={`colony-user-row-${u.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                  {u.email}
                  {u.disabled && (
                    <span className="text-[10px] uppercase tracking-wider bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-sm">
                      disabled
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.name || "—"}
                  {(u.colony_ids || []).length > 1 && ` · ${u.colony_ids.length} colonies`}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm"
                  onClick={() => resend(u)}
                  title="Send a fresh activation link"
                  data-testid={`colony-user-resend-${u.id}`}
                >
                  <Send className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm"
                  onClick={() => toggleDisabled(u)}
                  title={u.disabled ? "Re-enable this login" : "Disable this login"}
                  data-testid={`colony-user-toggle-${u.id}`}
                >
                  {u.disabled ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-sm text-destructive"
                  onClick={() => remove(u)}
                  data-testid={`colony-user-remove-${u.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="colony-users-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
